import React, { useMemo } from "react";

import { Box } from "@material-ui/core";

import { default as BaseModal } from "@ds-developer/modal";
import { EventType } from "@hubcapp/graphql";
import Button from "@ds-developer/button";
import RadioButtons from "@ds-developer/radio-buttons";

import { AddLessonModalProps } from "./types";
import { BookingPreviewModal } from "@hubcapp/components/Calendar/components/Modals/AddLessonModal/components/BookingPreviewModal";
import { LessonFormFields, UnavailableFormFields } from "./components";
import { SuccessBookingModal } from "@hubcapp/components/Calendar/components/Modals/AddLessonModal/components/SuccessBookingModal";
import { dateRangeIsValid } from "./helpers";

const AddLessonModal: React.FC<AddLessonModalProps> = ({
  learnersLoading = false,
  createEventLoading = false,
  isValid,
  open = false,
  submitting,
  learners,
  values,
  handleSubmit,
  handleChange,
  handleCancel,
  handleEventTypeChange,
  errors,
  touched,
  showPreviewBookingModal,
  timeSlotsAvailability,
  loadingTimeSlots,
  cancelPendingLessonsLoading,
  backPreviewBookingModalHandler,
  handleCreatePreBookedLessons,
  showSuccessBookingModal,
  lessonsNumber,
  convertPendingLessonsLoading,
}) => {
  const eventType = values.type;

  const submitDisabled = useMemo(
    () =>
      !isValid ||
      !dateRangeIsValid(values.startDateTime, values.endDateTime, values.allDay)
        .isValid ||
      submitting,
    [isValid, submitting, values.startDateTime, values.endDateTime],
  );

  const addLessonButtonText = values.repeatingLessonFrequencyNumber
    ? `Add ${values.repeatingLessonFrequencyNumber} lessons`
    : "Add lesson";

  const bookedTimeSlots = timeSlotsAvailability?.timeSlotsAvailability?.filter(
    timeSlot => timeSlot.availability === true,
  );

  const actions = (
    <>
      <Button disabled={submitting} variant="outlined" onClick={handleCancel}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color="primary"
        type="submit"
        form="notesForm"
        disabled={submitDisabled}
        loading={createEventLoading}
      >
        {eventType === EventType.Unavailable
          ? "Add unavailable slot"
          : addLessonButtonText}
      </Button>
    </>
  );

  if (showPreviewBookingModal) {
    return (
      <BookingPreviewModal
        backPreviewBookingModalHandler={backPreviewBookingModalHandler}
        loadingTimeSlots={loadingTimeSlots || convertPendingLessonsLoading}
        cancelPendingLessonsLoading={cancelPendingLessonsLoading}
        createEventLoading={createEventLoading}
        handleCreatePreBookedLessons={handleCreatePreBookedLessons}
        bookedTimeSlots={bookedTimeSlots}
        timeSlotsAvailability={timeSlotsAvailability}
        repeatingLessonFrequencyNumber={values.repeatingLessonFrequencyNumber}
        handleCancel={handleCancel}
      />
    );
  }

  if (showSuccessBookingModal) {
    return (
      <SuccessBookingModal
        lessonsNumber={lessonsNumber}
        closeModalHandler={handleCancel}
        handleSeeLessons={handleCancel}
        learnerId={showSuccessBookingModal}
      />
    );
  }

  return (
    <BaseModal
      actions={actions}
      open={open}
      title={
        eventType === EventType.Unavailable
          ? "Add an unavailable slot"
          : "Add a lesson"
      }
    >
      <form id="notesForm" onSubmit={handleSubmit}>
        <Box marginBottom={3}>
          <RadioButtons
            label="Type of calendar event"
            value={values?.type}
            onChange={val => {
              const event: React.ChangeEvent<HTMLInputElement> = {
                target: {
                  type: "text",
                  value: val,
                  name: "type",
                },
                persist() {},
              } as any;
              handleEventTypeChange(val);
              handleChange(event);
            }}
          >
            <Button value={EventType.PaidLesson}>Lesson</Button>
            <Button value={EventType.Unavailable}>Unavailable</Button>
          </RadioButtons>
        </Box>

        {eventType == EventType.Unavailable ? (
          <UnavailableFormFields
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
          />
        ) : (
          <LessonFormFields
            learnersLoading={learnersLoading}
            learners={learners}
            values={values}
            handleChange={handleChange}
          />
        )}
      </form>
    </BaseModal>
  );
};

export default AddLessonModal;
